import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCpdEnabled } from '@/contexts/TenantContext';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Line 
} from 'recharts';
import {
  Activity,
  BarChart3,
  Award,
  Flame,
  TrendingUp,
  CheckCircle,
  Target,
  GraduationCap,
  Lightbulb,
  Loader2,
  MessageSquare,
} from 'lucide-react';
import { fetchUserProgressData, fetchUserSimulations, fetchAgents } from '@/lib/api';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function MyProgress() {
  const { user } = useAuth();
  const cpdEnabled = useCpdEnabled();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<'week' | 'month' | 'year'>('month');
  const [loading, setLoading] = useState(true);
  const [progressData, setProgressData] = useState<any>(null);
  const [activities, setActivities] = useState<any[]>([]);
  const [recommendedSim, setRecommendedSim] = useState<{ id: string; name: string; title: string } | null>(null);

  useEffect(() => {
    async function loadProgress() {
      if (!user) return;
      setLoading(true);
      try {
        const progress = await fetchUserProgressData(user.id, { includeCpd: cpdEnabled });
        if (progress) {
          setProgressData(progress);
          
          // Check for new badges and show notifications
          const newBadges = progress.stats.badges.filter((badge: any) => {
            const earnedAt = new Date(badge.earnedAt);
            const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
            return earnedAt > fiveMinutesAgo;
          });
          
          newBadges.forEach((badge: any) => {
            toast.success(`🎉 Badge Earned: ${badge.name}`, {
              description: badge.description,
              duration: 5000,
            });
          });
        }

        // Fetch activity log
        let activityQuery = supabase
          .from('user_activity_log')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(20);

        if (!cpdEnabled) {
          activityQuery = activityQuery.neq('activity_type', 'cpd_earned');
        }

        const { data: activityData } = await activityQuery;
        
        if (activityData) setActivities(activityData);

        // Fetch a recommended simulation from actual agents
        try {
          const agents = await fetchAgents({ status: 'active', limit: 20, user_id: user.id });
          if (agents && agents.length > 0) {
            // Pick a random active agent the user hasn't completed yet, or just the first one
            const notCompleted = agents.filter((a: any) => !a.user_best_score || a.user_best_score === 0);
            const pick = notCompleted.length > 0 
              ? notCompleted[Math.floor(Math.random() * notCompleted.length)]
              : agents[0];
            setRecommendedSim({ id: pick.id, name: pick.name, title: pick.title || pick.specialty || '' });
          }
        } catch (err) {
          console.error('Error fetching recommended sim:', err);
        }
      } catch (error) {
        console.error('Error loading progress:', error);
        toast.error('Failed to load progress data');
      } finally {
        setLoading(false);
      }
    }
    loadProgress();
  }, [user, cpdEnabled]);

  const performanceData = useMemo(() => {
    if (!progressData) return [];
    return period === 'week' 
      ? progressData.weeklyData || []
      : period === 'month' 
      ? progressData.monthlyData || []
      : progressData.yearlyData || [];
  }, [period, progressData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading your progress...</p>
        </div>
      </div>
    );
  }

  if (!progressData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">No progress data available</p>
      </div>
    );
  }

  const stats = progressData.stats || {};
  const skills = progressData.skills || {};

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'simulation_completed': return <CheckCircle className="text-green-600" size={20} />;
      case 'badge_earned': return <Award className="text-yellow-600" size={20} />;
      case 'goal_reached': return <Target className="text-blue-600" size={20} />;
      case 'cpd_earned': return <GraduationCap className="text-purple-600" size={20} />;
      case 'streak_milestone': return <Flame className="text-orange-600" size={20} />;
      case 'coaching_completed': return <GraduationCap className="text-indigo-600" size={20} />;
      default: return <Activity className="text-gray-600" size={20} />;
    }
  };

  const getActivityBgColor = (type: string) => {
    switch (type) {
      case 'simulation_completed': return 'bg-green-100';
      case 'badge_earned': return 'bg-yellow-100';
      case 'goal_reached': return 'bg-blue-100';
      case 'cpd_earned': return 'bg-purple-100';
      case 'streak_milestone': return 'bg-orange-100';
      case 'coaching_completed': return 'bg-indigo-100';
      default: return 'bg-gray-100';
    }
  };

  const formatActivityDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="p-8 max-w-[1800px] mx-auto">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">My Progress</h1>
        <p className="text-lg text-gray-600">
          Track your learning journey and performance across all simulations
        </p>
      </div>

      {/* Overview Stats Grid - Now 6 cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
        {/* Simulations Completed */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E8F5F0] to-[#024223]/20 flex items-center justify-center">
              <Activity className="text-[#024223]" size={24} />
            </div>
            <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
              <TrendingUp size={16} />
              {stats.trendSimulations || '+0'}
            </span>
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">{stats.totalSimulations || 0}</div>
          <div className="text-sm text-gray-600">Simulations</div>
          <div className="text-xs text-gray-500 mt-2">This month: {stats.thisMonthCount || 0}</div>
        </div>

        {/* Coaching Sessions */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-50 to-indigo-100 flex items-center justify-center">
              <MessageSquare className="text-indigo-600" size={24} />
            </div>
            <span className="flex items-center gap-1 text-indigo-600 text-sm font-medium">
              <TrendingUp size={16} />
              {stats.trendCoaching || '+0'}
            </span>
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">{stats.totalCoaching || 0}</div>
          <div className="text-sm text-gray-600">Coaching Sessions</div>
          <div className="text-xs text-gray-500 mt-2">This month: {stats.thisMonthCoachingCount || 0}</div>
        </div>

        {/* Average Performance */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center">
              <BarChart3 className="text-blue-600" size={24} />
            </div>
            <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
              <TrendingUp size={16} />
              {stats.trendAvgScore || '+0%'}
            </span>
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">{stats.avgScore || 0}/100</div>
          <div className="text-sm text-gray-600">Avg Sim Score</div>
          <div className="text-xs text-gray-500 mt-2">Last 30 days</div>
        </div>

        {/* Avg Readiness Score */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center">
              <Target className="text-purple-600" size={24} />
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">{stats.avgReadinessScore || 0}%</div>
          <div className="text-sm text-gray-600">Avg Readiness</div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
            <div 
              className="bg-purple-600 h-2 rounded-full transition-all duration-500" 
              style={{ width: `${stats.avgReadinessScore || 0}%` }} 
            />
          </div>
        </div>

        {cpdEnabled ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center">
              <Award className="text-green-600" size={24} />
            </div>
            <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
              <TrendingUp size={16} />
              {stats.trendCPD || '+0'}
            </span>
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">
            {stats.cpdPoints || 0}/{stats.cpdTarget || 60}
          </div>
          <div className="text-sm text-gray-600">CPD Points</div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
            <div 
              className="bg-green-600 h-2 rounded-full transition-all duration-500" 
              style={{ width: `${Math.min(((stats.cpdPoints || 0) / (stats.cpdTarget || 60)) * 100, 100)}%` }} 
            />
          </div>
          </div>
        ) : null}

        {/* Current Streak */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center">
              <Flame className="text-orange-600" size={24} />
            </div>
            <span className="flex items-center gap-1 text-orange-600 text-sm font-medium">
              🔥 {(stats.streakStatus || 'active') === 'active' ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">{stats.currentStreak || 0} days</div>
          <div className="text-sm text-gray-600">Current Streak</div>
          <div className="text-xs text-gray-500 mt-2">Longest: {stats.longestStreak || 0} days</div>
        </div>
      </div>

      {/* Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content - Left */}
        <div className="lg:col-span-2 space-y-8">
          {/* Performance Trend Chart */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Performance Trend</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setPeriod('week')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    period === 'week' 
                      ? 'bg-[#024223] text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Week
                </button>
                <button
                  onClick={() => setPeriod('month')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    period === 'month' 
                      ? 'bg-[#024223] text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Month
                </button>
                <button
                  onClick={() => setPeriod('year')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    period === 'year' 
                      ? 'bg-[#024223] text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Year
                </button>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={performanceData}>
                <defs>
                  <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#024223" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#024223" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#666666', fontSize: 12 }}
                />
                <YAxis 
                  domain={[0, 100]}
                  tick={{ fill: '#666666', fontSize: 12 }}
                  label={{ value: 'Score', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #E0E0E0',
                    borderRadius: '8px',
                    padding: '12px'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="score" 
                  stroke="#024223" 
                  strokeWidth={3}
                  fillOpacity={1} 
                  fill="url(#colorScore)"
                  name="Score"
                />
                <Line 
                  type="monotone" 
                  dataKey="goal" 
                  stroke="#FF9500" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Goal"
                />
              </AreaChart>
            </ResponsiveContainer>

            {stats.totalSimulations > 0 ? (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <TrendingUp className="text-green-600 mt-1" size={20} />
                  <div>
                    <div className="font-semibold text-gray-900">
                      {stats.thisMonthCount > 0 ? 'Great Progress!' : 'Keep Going!'}
                    </div>
                    <div className="text-sm text-gray-700">
                      {stats.thisMonthCount > 0
                        ? `You've completed ${stats.thisMonthCount} simulation${stats.thisMonthCount > 1 ? 's' : ''} this month. Keep up the momentum!`
                        : 'Complete a simulation this month to keep your progress trending upward.'}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Lightbulb className="text-blue-600 mt-1" size={20} />
                  <div>
                    <div className="font-semibold text-gray-900">Get Started!</div>
                    <div className="text-sm text-gray-700">
                      Complete your first simulation to start tracking your performance trends.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Skills Breakdown - Only show when user has completed simulations */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Skills Assessment</h2>
            
            {skills && skills.length > 0 ? (
              <>
                <div className="space-y-6">
                  {skills.map((skill: any, idx: number) => (
                    <div key={idx}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{skill.name}</span>
                        <div className="flex items-center gap-3">
                          <span className={`text-sm font-medium ${skill.trend === 'up' ? 'text-green-600' : 'text-red-500'}`}>
                            {skill.trend === 'up' ? '↑' : '↓'}
                          </span>
                          <span className="text-lg font-bold text-gray-900">{skill.score}/100</span>
                        </div>
                      </div>
                      <div className="relative w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className="absolute top-0 left-0 h-3 rounded-full transition-all duration-500"
                          style={{ 
                            width: `${skill.score}%`,
                            backgroundColor: skill.score >= 80 ? '#16a34a' : skill.score >= 60 ? '#ca8a04' : '#dc2626'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Find weakest skill for focus area suggestion */}
                {(() => {
                  const weakest = [...skills].sort((a: any, b: any) => a.score - b.score)[0];
                  return weakest ? (
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Lightbulb className="text-blue-600 mt-1" size={20} />
                        <div>
                          <div className="font-semibold text-gray-900 mb-1">Focus Area</div>
                          <div className="text-sm text-gray-700">
                            Your "{weakest.name}" ({weakest.score}/100) has the most room for improvement. 
                            Consider taking simulations focused on strengthening this skill.
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null;
                })()}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <BarChart3 className="mx-auto mb-3" size={40} />
                <p className="font-medium text-gray-900 mb-1">No Skills Data Yet</p>
                <p className="text-sm">Complete your first simulation to see your skills breakdown.</p>
              </div>
            )}
          </div>

          {/* Recent Activity Timeline */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Recent Activity</h2>
            
            <div className="space-y-4">
              {activities.map((activity: any) => (
                <div key={activity.id} className="flex items-start gap-4 pb-4 border-b border-gray-100 last:border-0">
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getActivityBgColor(activity.activity_type)}`}>
                    {getActivityIcon(activity.activity_type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="font-medium text-gray-900">{activity.title}</div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">{formatActivityDate(activity.created_at)}</span>
                    </div>
                    {activity.score && (
                      <div className="text-lg font-bold text-[#024223] mb-1">{activity.score}/100</div>
                    )}
                    {activity.description && (
                      <div className="text-sm text-gray-600">{activity.description}</div>
                    )}
                    {activity.metadata?.badge_icon && (
                      <div className="text-2xl mt-1">{activity.metadata.badge_icon}</div>
                    )}
                  </div>
                </div>
              ))}
              
              {activities.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="mx-auto mb-2" size={32} />
                  <p>No recent activity yet. Complete your first simulation!</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - Right */}
        <div className="space-y-6">
          {/* Achievements */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Badges</h3>
            
            {stats.badges && stats.badges.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {stats.badges.slice(0, 6).map((badge: any) => (
                    <div 
                      key={badge.id}
                      className="p-3 border border-[#024223] bg-gradient-to-br from-[#E8F5F0] to-white shadow-sm rounded-lg text-center transition-all hover:scale-105"
                    >
                      <div className="text-3xl mb-1">{badge.icon}</div>
                      <div className="text-xs font-semibold text-gray-900 mb-1">{badge.name}</div>
                      <div className="text-xs text-gray-600 leading-tight line-clamp-2">{badge.description}</div>
                    </div>
                  ))}
                </div>

                <div className="text-center pt-4 border-t border-gray-200">
                  <div className="text-2xl font-bold text-gray-900">
                    {stats.badges.length}
                  </div>
                  <div className="text-sm text-gray-600">Total Badges Earned</div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Award className="mx-auto mb-2" size={32} />
                <p className="text-sm">Complete simulations to earn badges!</p>
              </div>
            )}
          </div>

          {/* Learning Recommendations */}
          <div className="bg-gradient-to-br from-[#024223] to-[#035A2F] text-white rounded-xl p-6">
            <h3 className="text-xl font-bold mb-4">Recommended Next</h3>
            
            <div className="space-y-3">
              <div className="bg-white/10 backdrop-blur rounded-lg p-4 border border-white/20">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb size={20} className="text-yellow-300" />
                  <span className="font-semibold">Focus Area</span>
                </div>
                <div className="text-sm opacity-90 mb-3">
                  Based on your performance, we recommend focusing on:
                </div>
                <div className="bg-white/10 rounded px-3 py-2 text-sm font-medium">
                  Closing & Follow-up Strategies
                </div>
              </div>

              {recommendedSim && (
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 border border-white/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Target size={20} className="text-blue-300" />
                    <span className="font-semibold">Try Next</span>
                  </div>
                  <div className="text-sm opacity-90 mb-2">
                    {recommendedSim.name}{recommendedSim.title ? ` - ${recommendedSim.title}` : ''}
                  </div>
                  <button 
                    onClick={() => navigate(`/simulation/${recommendedSim.id}`)}
                    className="w-full bg-white text-[#024223] py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors text-sm"
                  >
                    Start Simulation
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Goals */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Upcoming Goals</h3>
            
            <div className="space-y-4">
              <div className="border-l-4 border-[#024223] pl-4 py-2">
                <div className="font-semibold text-gray-900 mb-1">Complete 5 Simulations</div>
                <div className="text-sm text-gray-600 mb-2">Progress: {Math.min(stats.totalSimulations || 0, 5)}/5</div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-[#024223] h-2 rounded-full transition-all duration-500" style={{ width: `${Math.min(100, ((stats.totalSimulations || 0) / 5) * 100)}%` }} />
                </div>
              </div>

              {cpdEnabled ? (
                <div className="border-l-4 border-blue-600 pl-4 py-2">
                <div className="font-semibold text-gray-900 mb-1">Earn {stats.cpdTarget || 60} CPD Points</div>
                <div className="text-sm text-gray-600 mb-2">Progress: {stats.cpdPoints || 0}/{stats.cpdTarget || 60}</div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-500" 
                    style={{ width: `${Math.min(100, ((stats.cpdPoints || 0) / (stats.cpdTarget || 60)) * 100)}%` }} 
                  />
                </div>
                </div>
              ) : null}

              <div className="border-l-4 border-orange-600 pl-4 py-2">
                <div className="font-semibold text-gray-900 mb-1">Maintain 14-Day Streak</div>
                <div className="text-sm text-gray-600 mb-2">Current: {stats.currentStreak || 0} days 🔥</div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-orange-600 h-2 rounded-full transition-all duration-500" 
                    style={{ width: `${Math.min(100, ((stats.currentStreak || 0) / 14) * 100)}%` }} 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
